import React from 'react';

import cn from 'classnames';
import { Link } from 'react-router-dom';

import Card from 'components/Card';
import IconFemtasy from 'components/IconFemtasy';
import LoveIcon from 'components/LoveIcon';
import Message from 'components/Message';
import SpeakerTag from 'components/SpeakerTag';
import StoryBadges from 'components/StoryBadges';
import StoryContentCategoryLabel from 'components/StoryContentCategoryLabel';
import Typography from 'components/Typography';

import {
  CommonStoryTileFieldsFragment,
  Story,
} from 'graphql/generated/graphql';

import {
  putElementInFirstPosition,
  removeElementFromArray,
} from 'helpers/array';
import { localizeWithCurrentUrl } from 'helpers/locales';
import { saveItemPosition } from 'helpers/scrollPreservation';

import { useTracking } from 'hooks/useTracking';

import './style.scss';

type StoryListTileInteraction = 'click' | 'expand' | 'collapse';
export type StoryListTileInteractionHandler = (
  interaction: StoryListTileInteraction,
  story: Pick<Story, 'id' | 'relevanceScore'>,
  positionInList?: number,
) => void;

interface MatomoProps {
  name: string;
  piece?: string;
}

interface IProps {
  endpoint?: string;
  matomoProps?: MatomoProps;
  onInteraction?: StoryListTileInteractionHandler;
  positionInList: number;
  small?: boolean;
  story: Omit<CommonStoryTileFieldsFragment, 'loved'> & { loved?: boolean };
  expanded?: boolean;
}

const isSearchResult = (props?: MatomoProps) => {
  return props?.name === 'search' && props.piece === 'results';
};

const StoryListTile = ({
  endpoint,
  matomoProps,
  onInteraction,
  positionInList,
  small = false,
  story,
  expanded: initiallyExpanded = false,
}: IProps) => {
  const {
    alreadyListened,
    durationInMinutes,
    femtasyRed,
    contentType,
    id,
    languageIntensity,
    loved,
    releaseDate,
    speaker,
    themeTag,
    title,
  } = story;

  const [expanded, setExpanded] = React.useState<boolean>(initiallyExpanded);

  const { trackContentInteraction } = useTracking();

  const urlSuffix = isSearchResult(matomoProps) ? '?source=search' : '';

  const url = `/${endpoint}/${id}/details${urlSuffix}`;

  const tagsWithoutThemeTag = removeElementFromArray(story.tags, themeTag);

  const toggleExpandedDetails = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      event.stopPropagation();
      onInteraction?.(expanded ? 'collapse' : 'expand', story, positionInList);
      setExpanded(!expanded);
    },
    [setExpanded, expanded, onInteraction, story, positionInList],
  );

  const LanguageIntensity = () => (
    <>
      <Typography variant="small-thick" component="span">
        <Message
          message={{
            id: `common.languageIntensities.${languageIntensity.slug}`,
          }}
        />
      </Typography>

      <div className="StoryListTile__separator_vertical" />
    </>
  );

  return (
    <Card
      className={cn(
        'StoryListTile',
        expanded && `Card--${speaker.participantsType.slug}`,
        small && 'StoryListTile__small',
      )}
      data-cy="story-list-tile"
      data-testid="StoryListTile"
      allowOverflow
    >
      <Link
        to={localizeWithCurrentUrl(url)}
        className={cn(
          'StoryListTile__link',
          small && 'StoryListTile__link__full_height',
        )}
        onClick={() => {
          if (onInteraction) {
            onInteraction('click', story, positionInList);
          } else {
            const trackingCategory = matomoProps?.name || 'story_tile';

            trackContentInteraction(
              'click',
              trackingCategory,
              `${trackingCategory}_${positionInList}_${id}`,
              url,
            );
          }

          saveItemPosition(positionInList);
        }}
      >
        <div className="StoryListTile__badgesSpace">
          <StoryBadges stringDate={releaseDate} />
        </div>

        <div className="StoryListTile__speakerLabel">
          <StoryContentCategoryLabel
            contentType={contentType}
            showOnlyIcon={small}
            iconSize="20"
          />
          <SpeakerTag speaker={speaker} />
          {alreadyListened && (
            <IconFemtasy
              size="16"
              icon="Replayline"
              className="StoryListTile__speakerLabel__replay"
            />
          )}
        </div>

        <div className="StoryListTile__details">
          <Typography
            variant="h4"
            className={cn(
              'StoryListTile__title',
              expanded
                ? 'StoryListTile__title--expanded'
                : 'StoryListTile__title--collapsed',
              alreadyListened && 'StoryListTile__title--already-listened',
            )}
          >
            {title}
          </Typography>

          <div className="StoryListTile__details__info">
            <Typography variant="small-thin">{durationInMinutes}</Typography>

            {femtasyRed && (
              <div className="StoryListTile__explicit">
                <IconFemtasy icon="Lockline" size="14" />
                <Typography variant="small-thin">
                  <Message
                    message={{ id: 'components.StoryListTile.explicit' }}
                  />
                </Typography>
              </div>
            )}
          </div>
        </div>

        {small && <div className="StoryListTile__separator_horizontal" />}

        <div
          className={cn(
            'StoryListTile__bottom',
            expanded
              ? 'StoryListTile__bottom--expanded'
              : 'StoryListTile__bottom--collapsed',
          )}
        >
          {small && (
            <div className="StoryListTile__small__details">
              <LanguageIntensity />

              <Typography
                component="span"
                variant="small-thin"
                className="StoryListTile__tags"
              >
                {themeTag?.name || story.tags[0]?.name}
              </Typography>
            </div>
          )}

          {!small && expanded && (
            <>
              <LanguageIntensity />

              <Typography
                component="span"
                variant="small-thin"
                className="StoryListTile__tags"
              >
                {putElementInFirstPosition(tagsWithoutThemeTag, themeTag)
                  .map((tag) => tag?.name)
                  .join(', ')}
              </Typography>
            </>
          )}
        </div>

        {expanded && !small && (
          <div className="StoryListTile__content">
            <Typography
              variant="body"
              component="span"
              className="StoryListTile__expandedDetails"
            >
              {story.description}
            </Typography>

            <div className="StoryListTile__separator_horizontal" />
          </div>
        )}
      </Link>

      {/* collapsed bottom bar */}
      {!expanded && !small && (
        <div className="StoryListTile__content__bottom">
          <LanguageIntensity />

          <Typography
            component="span"
            variant="small-thin"
            className="StoryListTile__tags"
          >
            {themeTag?.name || story.tags[0]?.name}
          </Typography>

          <IconFemtasy
            icon="Arrowdown"
            size="24"
            onClick={toggleExpandedDetails}
          />
        </div>
      )}

      {/* expanded bottom bar */}
      {expanded && !small && (
        <div className="StoryListTile__content__bottom">
          <LoveIcon
            itemId={`${id}`}
            matomoName={matomoProps ? matomoProps.name : 'story-tile'}
            matomoPiece={matomoProps?.piece}
            loved={loved}
            playlists={story.playlists}
          />

          <IconFemtasy
            className="StoryListTile__content__bottom__arrow_up"
            icon="Arrowup"
            size="24"
            onClick={toggleExpandedDetails}
          />
        </div>
      )}
    </Card>
  );
};

export default StoryListTile;
